import type { DocumentTab } from 'features/knowledgeDocumentViewer/types'
import type { DocumentsSearchParams } from 'routes/documents/-searchParams'
import { getSearchStatePatchFromUrl } from 'routes/documents/-searchParams'

import {
  applyDocumentUrlIntentToTabs,
  applySearchUrlPatchToTabs,
  createInitialTab,
  restoreSearchTabForHistoryTraversal,
} from './tabStateUrlHelpers'

const tabMatchesUrlDocument = (
  tab: DocumentTab,
  documentId: string
): boolean => {
  const item = tab.history[tab.currentIndex]
  return item.type === 'document' && item.documentId === documentId
}

/** In-tab history writes the URL before React commits the new tab document.
 * Skip creating a tab for that echo; keep creating tabs for outside URL changes. */
export function shouldSkipSelfWrittenDocumentUrlReconcile(args: {
  urlDocumentId: string | undefined
  lastSelfWrittenDocumentId: string | undefined
  activeTabMatchesUrlDocument: boolean
}): boolean {
  if (args.urlDocumentId === undefined) return false
  if (args.lastSelfWrittenDocumentId !== args.urlDocumentId) return false
  return !args.activeTabMatchesUrlDocument
}

export function reconcileTabsWithDocumentsUrl(
  openTabs: DocumentTab[],
  activeTabId: string,
  search: DocumentsSearchParams,
  options?: { isHistoryTraversal?: boolean }
): { openTabs: DocumentTab[]; activeTabId: string } | null {
  const urlDocumentId = search.documentID
  const hasUrlDocument = !!urlDocumentId

  if (hasUrlDocument && urlDocumentId) {
    const activeTab = openTabs.find((tab) => tab.id === activeTabId)
    if (activeTab && tabMatchesUrlDocument(activeTab, urlDocumentId)) {
      const nextTabs = applyDocumentUrlIntentToTabs(
        openTabs,
        activeTab.id,
        search
      )
      if (nextTabs === openTabs) return null
      return { openTabs: nextTabs, activeTabId }
    }

    const existingTab = openTabs.find((tab) =>
      tabMatchesUrlDocument(tab, urlDocumentId)
    )
    if (existingTab) {
      return {
        openTabs: applyDocumentUrlIntentToTabs(
          openTabs,
          existingTab.id,
          search
        ),
        activeTabId: existingTab.id,
      }
    }
    const newTab = createInitialTab(
      urlDocumentId,
      search.pageNumber,
      search.selectedBoxes,
      search.selectedRegions
    )
    return {
      openTabs: [...openTabs, newTab],
      activeTabId: newTab.id,
    }
  }

  const urlPatch = getSearchStatePatchFromUrl(search)
  if (!urlPatch || Object.keys(urlPatch).length === 0) return null

  if (options?.isHistoryTraversal) {
    return restoreSearchTabForHistoryTraversal(
      openTabs,
      activeTabId,
      urlPatch.query
    )
  }

  return applySearchUrlPatchToTabs(openTabs, activeTabId, urlPatch)
}
